import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext({
  lang: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

const translations = {
  en: {
    nav: {
      experience: 'Experience',
      skills: 'Skills',
      education: 'Education',
      services: 'Services',
      twitter: 'Twitter',
      github: 'Github',
      instagram: 'Instagram',
      linkedin: 'LinkedIn',
      homeAlt: 'Samir Djelal',
      searchPlaceholder: 'Search skills...',
      noSkills: 'No skills found',
      tryAdjust: "Try adjusting your search or filter to find what you're looking for.",
      clearSearch: 'Clear search',
      contact: 'Contact',
      cv: 'CV',
    },
    sections: {
      heroTitle: 'Samir Djelal',
      heroSubtitle: 'Group Chief Technology & Solution Officer • Systems & Security Architect',
      heroIntro:
        "Technical leader and solutions architect leading a shared resource hub across fintech, healthtech, and cybersecurity entities. I specialize in building high-throughput payment gateways, mission-critical infrastructure, and deterministic automation workflows under strict regulatory compliance (PCI DSS, ISO 27001, ADHICS).",
      skillsTitle: 'Engineering & Leadership Pillars',
      skillsStorySubtitle: 'Proven execution, regulatory milestones, and real-world system architecture.',
      executiveBannerTitle: 'Executive Leadership & Compliance Posture',
      problemTitle: 'The Engineering Challenge',
      approachTitle: 'Architectural Approach',
      impactTitle: 'Measured Impact & Milestones',
      stackTitle: 'Core Stack in Practice',
      filterAll: 'All Domains',
      all: 'All',
      experienceTitle: 'Professional Experience',
      educationTitle: 'Education',
      servicesTitle: 'Services & Apps',
      servicesDesc: 'A collection of my free-to-use apps and services.',
      defaultSkillDesc: 'A skill in my technical toolkit.',
      // Home (landing) specific translations
      interestedIn: 'Interested In',
      interestedSubtitle: 'Domains, emerging paradigms, and technical challenges I continuously explore and build for.',
      interestedList: [
        'High-Concurrency Systems (Rust & Go)',
        'Payment Gateway Architecture & Tokenization',
        'End-to-End Compliance (PCI DSS, ISO 27001)',
        'Perimeter Security (CrowdSec, Suricata, WAF)',
        'Observability & Fleet Telemetry (Trax)',
        'Multi-Cloud Crisis DR & Live Migration',
        'Deterministic Workflow Automation (n8n)',
        'Enterprise Architecture & Shared Hub Leadership',
        'High-Performance Modern Web Platforms',
        'Autonomous AI Agents & Agentic Workflows',
        'Custom MCP Server Development',
        'Advanced Prompt Engineering & LLM Tools'
      ],
      // Fallback legacy keys (kept for backward compatibility)
      interested1: 'AI Agents & Autonomous Systems',
      interested2: 'Advanced Prompt Engineering',
      interested3: 'AI Tooling & Automation',
      interested4: 'Enterprise Architecture',
      interested5: 'DevSecOps & Cloud Security',
      interested6: 'Observability & Tracking',
      interested7: 'CI/CD & Engineering',
      focusOnTitle: 'I Focus On',
      focusSubtitle: 'Core priorities guiding my architectural decisions and daily engineering execution.',
      focus1: 'High-Load Scalability, Reliability & Crisis DR 🛡️',
      focus2: 'Ultra-Low Latency & High-Throughput Performance ⚡',
      focus3: 'Security Governance & Enterprise Architecture 🧩',
      focusList: [
        {
          title: 'High-Load Scalability, Reliability & Crisis DR',
          desc: 'Architecting fault-tolerant infrastructure and battle-tested disaster recovery runbooks that guarantee business continuity and zero data loss under peak load.',
          tag: 'Resilience'
        },
        {
          title: 'Ultra-Low Latency & High-Throughput Systems',
          desc: 'Engineering sub-second transaction gateways in Rust and high-efficiency APIs in Laravel & Go, backed by optimized caching and database indexing.',
          tag: 'Performance'
        },
        {
          title: 'Security Governance & Regulatory Compliance',
          desc: 'Embedding end-to-end security controls (PCI DSS v4.0.1, ISO 27001, ADHICS, WAF, IDS/IPS) directly into code pipelines and infrastructure topologies.',
          tag: 'Compliance'
        },
        {
          title: 'Deterministic Workflow Automation & Autonomous Systems',
          desc: 'Eliminating manual operational overhead via self-healing data pipelines, custom MCP servers, n8n flows, and autonomous task-oriented agents.',
          tag: 'Automation'
        }
      ],
      favoriteLanguages: 'Favorite Languages',
      connectTitle: "Let's Connect",
      connectDesc:
        "I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision. Feel free to reach out through any of my social media channels.",
      // SummarySection (home) specific
      cvDownloadTitle: 'Executive Curriculum Vitae',
      cvDownloadDesc: 'Complete executive dossier covering systems architecture, high-throughput engineering (Rust, Laravel), regulatory remediation (PCI DSS v4.0.1, ISO 27001), and crisis disaster recovery.',
      cvProExp: 'Executive Leadership & Engineering',
      cvEduDetails: 'Verified Diplomas & Credentials',
      cvTechSkills: 'Systems, Security & Perimeter Defense',
      recentExperienceTitle: 'Recent Experience',
      educationTitleSimple: 'Education',
      keySkillsTitle: 'Key Skills',
      viewAll: 'View All →',
      cvDownloadButton: 'Download Executive CV (PDF)',
      moreSuffix: 'more',
      contactTitle: 'Get in Touch',
      contactSubtitle: "Have a project in mind? Reach out through any of these channels.",
      contactNameLabel: 'Full Name',
      contactEmailLabel: 'Email Address',
      contactMessageLabel: 'Your Message',
      contactSubmitButton: 'Send Message',
      contactSuccessMessage: 'Message sent successfully! I will get back to you soon.',
      contactInfoTitle: 'Direct Contact',
      contactInfoDesc: 'I am available for freelance opportunities, full-time positions, or professional collaborations.',
      contactSocialTitle: 'Social Media',
      contactSocialDesc: 'Follow my professional updates and insights through these platforms.',
      contactAvailabilityTitle: 'Working Hours',
      contactAvailabilityDesc: 'I am usually responsive within 24 hours during business days (Mon-Fri).',
      contactLocationTitle: 'Based In',
    },
  },
  ar: {
    nav: {
      experience: 'الخبرة',
      skills: 'المهارات',
      education: 'التعليم',
      services: 'الخدمات',
      twitter: 'تويتر',
      github: 'Github',
      instagram: 'إنستغرام',
      linkedin: 'لينكدإن',
      homeAlt: 'سمير جلال',
      searchPlaceholder: 'ابحث عن مهارة...',
      noSkills: 'لا توجد مهارات',
      tryAdjust: 'حاول تعديل البحث أو عامل التصفية للعثور على ما تبحث عنه.',
      clearSearch: 'مسح البحث',
      contact: 'اتصل بي',
      cv: 'السيرة الذاتية',
    },
    sections: {
      heroTitle: 'سمير جلال',
      heroSubtitle: 'رئيس قطاع التكنولوجيا والحلول للمجموعة • معمارية الأنظمة والأمن السيبراني',
      heroIntro:
        'قائد تقني ومهندس حلول أدير مركز خدمات تكنولوجية مشترك عبر كيانات رائدة في مجالات التقنية المالية، الرعاية الصحية، والأمن السيبراني. متخصص في بناء بوابات الدفع فائقة الأداء، البنى التحتية الحيوية، وهندسة مسارات الأتمتة الموثوقة وفق أعلى المعايير التنظيمية العالمية (PCI DSS، ISO 27001، ADHICS).',
      skillsTitle: 'محاور القيادة والهندسة التقنية',
      skillsStorySubtitle: 'تنفيذ معماري عملي، إنجازات تنظيمية معتمدة، وموثوقية في بيئات الإنتاج الحقيقية.',
      executiveBannerTitle: 'القيادة التنفيذية ومستوى الامتثال التنظيمي',
      problemTitle: 'التحدي الهندسي',
      approachTitle: 'النهج المعماري',
      impactTitle: 'الأثر المحقق والإنجازات',
      stackTitle: 'حزمة التقنيات المستخدمة عملياً',
      filterAll: 'كافة المجالات',
      all: 'الكل',
      experienceTitle: 'الخبرات المهنية',
      educationTitle: 'التعليم',
      servicesTitle: 'الخدمات والتطبيقات',
      servicesDesc: 'مجموعة من تطبيقاتي وخدماتي المجانية.',
      defaultSkillDesc: 'مهارة ضمن صندوق أدواتي التقنية.',
      // Home (landing) specific translations
      interestedIn: 'مهتم بـ',
      interestedSubtitle: 'المجالات والاتجاهات الهندسية والتحديات التقنية التي أواصل استكشافها وبناء الحلول لها.',
      interestedList: [
        'الأنظمة فائقة التزامن (Rust و Go)',
        'معمارية بوابات الدفع والترميز المالي',
        'الامتثال التنظيمي الشامل (PCI DSS، ISO 27001)',
        'أمن محيط السحابة (CrowdSec، Suricata، WAF)',
        'المراقبة الموحدة والتحكم في الخوادم (منصة Trax)',
        'التعافي من الكوارث متعدد السحابات والترحيل دون فقدان للبيانات',
        'أتمتة الأعمال الموثوقة (n8n ومسارات الويب)',
        'المعمارية المؤسسية وقيادة مراكز الموارد المشتركة',
        'منصات الويب الحديثة فائقة الأداء',
        'وكلاء الذكاء الاصطناعي ومسارات العمل المستقلة',
        'تطوير خوادم MCP (Model Context Protocol)',
        'هندسة الأوامر المتقدمة وأنظمة النماذج اللغوية'
      ],
      // مفاتيح قديمة للاحتفاظ بالتوافق مع الواجهة الحالية
      interested1: 'وكلاء الذكاء الاصطناعي والأنظمة المستقلة',
      interested2: 'هندسة الأوامر المتقدمة',
      interested3: 'أدوات الذكاء الاصطناعي والأتمتة',
      interested4: 'Enterprise Architecture',
      interested5: 'DevSecOps وأمن السحابة',
      interested6: 'المراقبة وتتبع الأخطاء',
      interested7: 'CI/CD وهندسة المنصات',
      focusOnTitle: 'أركّز على',
      focusSubtitle: 'الأولويات الأساسية التي توجّه قراراتي المعمارية وتنفيذي الهندسي اليومي.',
      focus1: 'استيعاب أقصى أحمال التشغيل والتعافي السريع من الأزمات 🛡️',
      focus2: 'الأداء الفائق والسرعة في أجزاء من الثانية ⚡',
      focus3: 'الحوكمة الأمنية والمعمارية المؤسسية النظيفة 🧩',
      focusList: [
        {
          title: 'استيعاب أقصى أحمال التشغيل والتعافي الفوري من الأزمات',
          desc: 'تصميم بنى تحتية فائقة الموثوقية وخطط تعافٍ مجربة تضمن استمرارية الأعمال وصفر فقدان في البيانات تحت أعلى درجات الضغط التشغيلي.',
          tag: 'الموثوقية'
        },
        {
          title: 'الأداء فائق السرعة ومعالجة المعاملات في أجزاء من الثانية',
          desc: 'بناء بوابات معالجة مالية متقدمة بلغة Rust وواجهات برمجية بلغة Laravel و Go، مدعومة باستراتيجيات تخزين مؤقت وفهرسة متقدمة.',
          tag: 'الأداء الفائق'
        },
        {
          title: 'الحوكمة الأمنية الصارمة والامتثال التنظيمي المعتمد',
          desc: 'دمج ضوابط الأمان الشاملة (PCI DSS v4.0.1، ISO 27001، ADHICS، WAF، IDS/IPS) مباشرة في شيفرات البرمجة وطوبولوجيا الخوادم.',
          tag: 'الامتثال'
        },
        {
          title: 'الأنظمة المستقلة وأتمتة مسارات العمل الموثوقة',
          desc: 'إلغاء الهدر التشغيلي اليدوي عبر مسارات بيانات ذاتية الإصلاح، وخوادم MCP مخصصة، وأتمتة n8n، ووكلاء مهام أذكياء.',
          tag: 'الأتمتة'
        }
      ],
      favoriteLanguages: 'اللغات المفضلة',
      connectTitle: 'لنتواصل',
      connectDesc:
        'أنا دائمًا منفتح لمناقشة مشاريع جديدة أو أفكار إبداعية أو فرص لأكون جزءًا من رؤيتك. لا تتردد في التواصل عبر أي من قنوات التواصل الاجتماعي الخاصة بي.',
      // SummarySection (home) specific
      cvDownloadTitle: 'السيرة الذاتية التنفيذية (CV)',
      cvDownloadDesc: 'ملف تنفيذي متكامل يغطي معمارية الأنظمة، هندسة المنصات فائقة السرعة (Rust، Laravel)، والامتثال التنظيمي (PCI DSS v4.0.1، ISO 27001) وإدارة كوارث السحابة.',
      cvProExp: 'القيادة التنفيذية وهندسة الأنظمة',
      cvEduDetails: 'المؤهلات والدبلومات المعتمدة',
      cvTechSkills: 'الأنظمة، الأمن السيبراني وحماية المحيط',
      recentExperienceTitle: 'أحدث الخبرات',
      educationTitleSimple: 'التعليم',
      keySkillsTitle: 'أهم المهارات',
      viewAll: 'عرض الكل →',
      cvDownloadButton: 'تحميل السيرة التنفيذية (PDF)',
      moreSuffix: 'أخرى',
      contactTitle: 'تواصل معي',
      contactSubtitle: 'هل لديك مشروع في ذهنك؟ تواصل معي عبر أي من هذه القنوات.',
      contactNameLabel: 'الاسم الكامل',
      contactEmailLabel: 'البريد الإلكتروني',
      contactMessageLabel: 'رسالتك',
      contactSubmitButton: 'إرسال الرسالة',
      contactSuccessMessage: 'تم إرسال الرسالة بنجاح! سأرد عليك قريباً.',
      contactInfoTitle: 'اتصال مباشر',
      contactInfoDesc: 'أنا متاح لفرص العمل الحر، والوظائف بدوام كامل، أو التعاون المهني.',
      contactSocialTitle: 'وسائل التواصل الاجتماعي',
      contactSocialDesc: 'تابع تحديثاتي المهنية وآرائي عبر هذه المنصات.',
      contactAvailabilityTitle: 'ساعات العمل',
      contactAvailabilityDesc: 'عادة ما أرد خلال 24 ساعة في أيام العمل (الاثنين - الجمعة).',
      contactLocationTitle: 'المقر الرئيسي',
    },
  },
};

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive language from URL
  const lang = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts[0] === 'ar') return 'ar';
    return 'en';
  }, [location.pathname]);

  const setLanguage = (newLang) => {
    if (newLang === lang) return;

    const pathParts = location.pathname.split('/').filter(Boolean);
    let newPath;

    if (newLang === 'ar') {
      // Add 'ar' prefix
      newPath = `/ar/${pathParts.join('/')}`;
    } else {
      // Remove 'ar' prefix
      if (pathParts[0] === 'ar') {
        newPath = `/${pathParts.slice(1).join('/')}`;
      } else {
        newPath = `/${pathParts.join('/')}`;
      }
    }
    
    // Clean up double slashes and ensure root
    newPath = newPath.replace(/\/+/g, '/');
    if (newPath === '') newPath = '/';

    navigate(newPath);
  };

  useEffect(() => {
    const isRTL = lang === 'ar';
    const html = document.documentElement;
    if (html) {
      html.setAttribute('lang', lang);
      html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }
    document.body.dir = isRTL ? 'rtl' : 'ltr';
  }, [lang]);

  const t = useMemo(() => {
    const dict = translations[lang] || translations.en;
    return (key) => {
      const parts = key.split('.');
      let cur = dict;
      for (const p of parts) {
        if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
        else return key;
      }
      // Return the actual value (string, array, object). Fallback to key if not found.
      return cur;
    };
  }, [lang]);

  const value = useMemo(() => ({ lang, setLanguage, t }), [lang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
